import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/solid';
import React, { useState } from 'react';

import ConfirmableButton from '../../../components/ConfirmableButton';
import Filename from '../../../components/Filename';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { UploadedReceipt } from '../../../types';
import { BookingCardState } from '../hooks/useBookingCardState';
import { formatCurrency, formatDate } from '../utils/formatters';

import BookingCardActions from './BookingCardActions';
import ReceiptStatusBadge from './ReceiptStatusBadge';

interface BookingCardHeaderProps {
  receipt: UploadedReceipt;
  state: BookingCardState;
  onToggleExpanded: () => void;
  onImport: () => Promise<void>;
  onReject: () => Promise<void>;
  onRemove?: (receiptId: string) => void;
  isRemoving?: boolean;
  isDisabled: boolean;
}

const BookingCardHeader: React.FC<BookingCardHeaderProps> = ({
  receipt,
  state,
  onToggleExpanded,
  onImport,
  onReject,
  onRemove,
  isRemoving = false,
  isDisabled,
}) => {
  const [copiedField, setCopiedField] = useState<'name' | 'id' | null>(null);

  const handleCopy =
    (text: string, field: 'name' | 'id') => (e: React.MouseEvent) => {
      e.stopPropagation();
      navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    };

  const NON_REMOVABLE_STATUSES = [
    'import-scheduled',
    'importing',
    'imported',
    'import-failed',
  ];
  const canRemove =
    onRemove &&
    !state.isImportScheduling &&
    !NON_REMOVABLE_STATUSES.includes(receipt.status);
  return (
    <div className='flex items-center justify-between p-4 bg-gray-100 rounded-t-lg'>
      <button
        className='flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80 text-left'
        onClick={onToggleExpanded}
        type='button'
        aria-expanded={state.isExpanded}
      >
        {/* Expand/Collapse Icon */}
        <span className='text-gray-500'>
          {state.isExpanded ? (
            <ChevronUpIcon className='w-5 h-5' />
          ) : (
            <ChevronDownIcon className='w-5 h-5' />
          )}
        </span>

        {/* File Name */}
        <div className='flex-1'>
          <div className='flex items-center gap-1'>
            <Filename
              storageKey={receipt.storageKey}
              fileName={receipt.originalFileName}
              className='text-sm font-medium'
            />
            <button
              type='button'
              onClick={handleCopy(receipt.originalFileName, 'name')}
              className='p-0.5 text-gray-400 hover:text-gray-600 rounded transition-colors'
              title='Copy file name'
            >
              {copiedField === 'name' ? (
                <ClipboardDocumentCheckIcon className='w-3.5 h-3.5 text-green-500' />
              ) : (
                <ClipboardDocumentIcon className='w-3.5 h-3.5' />
              )}
            </button>
          </div>
          <div className='flex items-center gap-1'>
            <p className='text-xs text-gray-400 font-mono'>{receipt.id}</p>
            <button
              type='button'
              onClick={handleCopy(receipt.id, 'id')}
              className='p-0.5 text-gray-400 hover:text-gray-600 rounded transition-colors'
              title='Copy receipt ID'
            >
              {copiedField === 'id' ? (
                <ClipboardDocumentCheckIcon className='w-3 h-3 text-green-500' />
              ) : (
                <ClipboardDocumentIcon className='w-3 h-3' />
              )}
            </button>
          </div>
          {!state.isExpanded && state.updatedBookingData && (
            <div className='text-xs text-gray-500 mt-1 space-y-0.5'>
              <div>
                {state.updatedBookingData.contactName} •{' '}
                {formatCurrency(
                  state.updatedBookingData.totalGrossAmount,
                  state.updatedBookingData.currency
                )}{' '}
                • {formatDate(state.updatedBookingData.voucherDate)}
              </div>
              {state.updatedBookingData.remark && (
                <div className='italic truncate'>
                  {state.updatedBookingData.remark}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Loading indicator while import/reject API call is in flight */}
        {(state.isImportScheduling || state.isRejecting) && (
          <LoadingSpinner size='md' className='flex-shrink-0' />
        )}

        {/* Status Badge - Show for all booking review statuses, hidden during API call */}
        {!state.isImportScheduling &&
          !state.isRejecting &&
          (state.isImported ||
            state.isRejected ||
            state.hasImportingStatus ||
            receipt.status === 'generated' ||
            receipt.status === 'modified' ||
            receipt.status === 'text-extracting' ||
            receipt.status === 'import-failed') && (
            <ReceiptStatusBadge
              status={receipt.status}
              labelOverride={
                state.bookingSystem === 'datev' && state.isImported
                  ? 'Saved'
                  : undefined
              }
            />
          )}

        {/* Spinner for text-extracting status */}
        {receipt.status === 'text-extracting' && (
          <LoadingSpinner
            size='md'
            color='border-yellow-600 dark:border-yellow-400'
            className='flex-shrink-0'
          />
        )}
      </button>

      {/* Action Buttons - Only show if not imported/rejected/importing and not editing */}
      {!state.isImported &&
        !state.isRejected &&
        !state.hasImportingStatus &&
        !state.hasGeneratingStatus &&
        !state.isImportScheduling &&
        !state.isEditing && (
          <BookingCardActions
            onImport={onImport}
            onReject={onReject}
            isImporting={state.isImportScheduling}
            isRejecting={state.isRejecting}
            hasMissingCategories={state.hasMissingCategories}
            isDisabled={isDisabled}
            showImportButton={
              state.bookingSystem === 'datev'
                ? state.hasDatevChanges && state.showImportButton
                : state.showImportButton
            }
            showRejectButton={state.showRejectButton}
            onShowImportButton={state.setShowImportButton}
            onShowRejectButton={state.setShowRejectButton}
            onShowValidation={state.setShowValidation}
            importButtonLabel={
              state.bookingSystem === 'datev' ? 'Save' : 'Import'
            }
          />
        )}

      {/* Remove Button */}
      {canRemove &&
        (isRemoving ? (
          <div className='p-2'>
            <LoadingSpinner
              size='md'
              color='border-red-600 dark:border-red-400'
            />
          </div>
        ) : (
          <ConfirmableButton
            onConfirm={() => onRemove(receipt.id)}
            buttonClassName='p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200'
            confirmButtonClassName='p-1 bg-white dark:bg-gray-700 rounded-full border border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20'
            cancelButtonClassName='p-1 bg-white dark:bg-gray-700 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'
            containerClassName='flex gap-1'
          >
            <TrashIcon className='w-5 h-5' />
          </ConfirmableButton>
        ))}
    </div>
  );
};

export default BookingCardHeader;
